import {
  type PropertyVentilation as DbPropertyVentilation,
  VENTILATION_PLANS_ANSWERS,
  ventilationPlansDeliveryEnum,
  visitPreInfos,
} from '@markku/db'
import { sortBy } from '@markku/utils'
import { createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import type { PlanPin } from './attachment'

export const ventilationPlansSchema = z.enum(VENTILATION_PLANS_ANSWERS)
export type VentilationPlansAnswer = z.infer<typeof ventilationPlansSchema>

export const ventilationPlansDeliverySchema = z.enum(
  ventilationPlansDeliveryEnum.enumValues,
)
export type VentilationPlansDelivery = z.infer<
  typeof ventilationPlansDeliverySchema
>

export const selectVisitPreInfoSchema = createSelectSchema(visitPreInfos, {
  internalReviewedAt: z.string().nullable().optional(),
  internalReviewedBy: z.number().nullable().optional(),
  internalReviewComment: z.string().nullable().optional(),
})
export type VisitPreInfo = z.infer<typeof selectVisitPreInfoSchema>

export const MAX_VENTILATION_UNITS = 10

export const ventilationUnitSchema = z.object({
  brand: z.string().max(120).optional(),
  model: z.string().max(120).optional(),
  location: z.string().max(200).optional(),
})
export type VentilationUnit = z.infer<typeof ventilationUnitSchema>

/** The shape of `properties.ventilation`; kept in step with the db type. */
export const propertyVentilationSchema = z.object({
  units: z.array(ventilationUnitSchema).max(MAX_VENTILATION_UNITS),
  hasPlans: ventilationPlansSchema.nullish(),
  accessNotes: z.string().max(1000).nullish(),
  notes: z.string().max(2000).nullish(),
}) satisfies z.ZodType<DbPropertyVentilation>
export type PropertyVentilation = z.infer<typeof propertyVentilationSchema>

export const EMPTY_VENTILATION: PropertyVentilation = { units: [] }

export const preInfoAnswersSchema = z.object({
  units: z.array(ventilationUnitSchema).max(MAX_VENTILATION_UNITS),
  hasPlans: ventilationPlansSchema.optional(),
  plansDelivery: ventilationPlansDeliverySchema.optional(),
  pets: z.string().max(500).optional(),
  accessConfirmed: z.boolean().optional(),
  accessNotes: z.string().max(1000).optional(),
  notes: z.string().max(2000).optional(),
})
export type PreInfoAnswers = z.infer<typeof preInfoAnswersSchema>

export const sendPreInfoRequestSchema = z.object({
  visitId: z.number(),
})

export const markPreInfoReviewedSchema = z.object({
  visitId: z.number(),
  reviewComment: z.string().optional(),
})

export const updateVentilationProfileSchema = propertyVentilationSchema.extend({
  propertyId: z.number(),
  pets: z.string().max(500).nullish(),
})

export const VENTILATION_PLANS_LABELS: Record<VentilationPlansAnswer, string> =
  {
    yes: 'Kyllä',
    no: 'Ei',
    unknown: 'Ei tiedä',
  }

export const VENTILATION_PLANS_DELIVERY_LABELS: Record<
  VentilationPlansDelivery,
  string
> = {
  uploaded: 'Lähetti kuvat',
  onsite: 'Etsii käyntipäiväksi',
}

export const PRE_INFO_ATTACHMENT_TYPES = {
  PLAN: 'ventilation_plan',
  UNIT_PLATE: 'ventilation_unit_plate',
} as const

export type PreInfoAttachmentType =
  (typeof PRE_INFO_ATTACHMENT_TYPES)[keyof typeof PRE_INFO_ATTACHMENT_TYPES]

interface PlanAttachment {
  id: number
  mimeType: string
  createdAt: string
  metadata?: Record<string, unknown> | null
}

/**
 * The plans a pin can go on. A PDF plan only becomes one once a page of it has
 * been saved as an image (`sourceAttachmentId` + `page` in its metadata).
 */
export const floorPlanImages = <T extends PlanAttachment>(attachments: T[]) =>
  attachments.filter(
    (a) =>
      a.metadata?.type === PRE_INFO_ATTACHMENT_TYPES.PLAN &&
      a.mimeType.startsWith('image/'),
  )

export const attachmentPin = (attachment: PlanAttachment) =>
  attachment.metadata?.pin as PlanPin | undefined

// Upsell photos are internal sales notes: their pins stay off the report and
// the portal, and out of the customer's numbering.
const CUSTOMER_MEDIA_TYPES = ['before', 'after', 'defect', 'visit_video']

export const isCustomerMedia = (attachment: PlanAttachment) =>
  CUSTOMER_MEDIA_TYPES.includes(String(attachment.metadata?.type))

// Pins closer than this, as a fraction of the plan, are one spot: a valve's
// before and after photo tapped a fingertip apart.
const SAME_SPOT_DISTANCE = 0.02

/**
 * The customer's marked spots on one plan, numbered by when the photos were
 * taken: that follows the technician's walk, so neighbouring rooms get
 * neighbouring numbers. Pinning an older photo later renumbers the spots after
 * it. A photo pinned at or next to an earlier photo's pin joins that spot,
 * which sits where its earliest photo was pinned.
 */
export const planSpots = <T extends PlanAttachment>(
  attachments: T[],
  planId: number,
) => {
  const pinned = sortBy(
    attachments.filter(
      (a) => attachmentPin(a)?.planId === planId && isCustomerMedia(a),
    ),
    (a) => a.createdAt,
  )
  const spots: Array<{ x: number; y: number; attachments: T[] }> = []
  for (const attachment of pinned) {
    const { x, y } = attachmentPin(attachment) as PlanPin
    const near = spots.find(
      (spot) => Math.hypot(spot.x - x, spot.y - y) < SAME_SPOT_DISTANCE,
    )
    if (near) near.attachments.push(attachment)
    else spots.push({ x, y, attachments: [attachment] })
  }
  return spots.map((spot, index) => ({ number: index + 1, ...spot }))
}

/** The plans the report and the customer's view show: those with a spot. */
export const pinnedPlans = <T extends PlanAttachment>(attachments: T[]) =>
  floorPlanImages(attachments)
    .map((plan) => ({ plan, spots: planSpots(attachments, plan.id) }))
    .filter(({ spots }) => spots.length)
